import type { SelectAllMatches, TableSelection } from '@trm/ui-kit-admin';

export const clearedSelection = (state: { selectionVersion: number }) => ({
  selectedIds: new Set<number>() as ReadonlySet<number>,
  selectionError: null,
  selectionVersion: state.selectionVersion + 1,
});

export const limitedSelection = (
  current: ReadonlySet<number>,
  next: ReadonlySet<number>,
  limit: number,
  plural: string,
  limitMessage = `Select no more than ${limit} ${plural} for a bulk action.`,
) =>
  next.size <= limit
    ? { selectedIds: next, selectionError: null }
    : {
        selectedIds: current,
        selectionError: limitMessage,
      };

export const toggleSelected = (current: ReadonlySet<number>, id: number): ReadonlySet<number> =>
  current.has(id)
    ? new Set([...current].filter((value) => value !== id))
    : new Set([...current, id]);

export const selectMany = (
  current: ReadonlySet<number>,
  ids: readonly number[],
  selected: boolean,
): ReadonlySet<number> =>
  selected
    ? new Set([...current, ...ids])
    : new Set([...current].filter((id) => !ids.includes(id)));

export const tableSelection = (
  visibleIds: readonly number[],
  selectedIds: ReadonlySet<number>,
  toggle: (id: number) => void,
  setMany: (ids: readonly number[], selected: boolean) => void,
  label: string,
): TableSelection => {
  const all = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  return {
    isRowSelected: (id) => typeof id === 'number' && selectedIds.has(id),
    onRowToggle: (id) => {
      if (typeof id === 'number') toggle(id);
    },
    isAllSelected: all,
    isSomeSelected: !all && visibleIds.some((id) => selectedIds.has(id)),
    onToggleAll: () => setMany(visibleIds, !all),
    'aria-label': label,
  };
};

export const completeMatchingIds = (
  ids: readonly number[],
  total: number,
  limit: number,
): boolean => total <= limit && ids.length === total && new Set(ids).size === total;

export const selectAllMatches = ({
  total,
  selectedCount,
  limit,
  plural,
  isSearchPending,
  isSelecting,
  onSelectAll,
}: {
  total: number;
  selectedCount: number;
  limit: number;
  plural: string;
  isSearchPending: boolean;
  isSelecting: boolean;
  onSelectAll: () => void;
}): SelectAllMatches => {
  if (total > limit)
    return {
      status: 'unavailable',
      reason: `This filter matches ${total.toLocaleString('en-US')} ${plural} and one action covers ${limit.toLocaleString('en-US')}. Narrow the filter.`,
    };
  if (isSearchPending) return { status: 'unavailable', reason: 'Wait for the search to finish.' };
  if (isSelecting) return { status: 'selecting' };
  if (total > 0 && selectedCount >= total) return { status: 'selected' };

  return { status: 'available', onSelectAll };
};
